// WHO GOES FIRST on a scarce key, and how each origin spends one — the lane and rank
// tables capacity admission (`./key-capacity.ts`) reads. Split out of that module when
// it outgrew the size budget (#6228); it re-exports every name here, so each import
// path is unchanged. Pure, and it depends on nothing in that module, so the two cannot
// form an initialisation cycle.

import { TaskClass, TaskOrigin } from './task-envelope';

/** How a task spends a key: its unit count and whether it may use the review reserve. */
export const KeyAdmissionLane = {
  /** A PR-producing harness lane: holds its editor fan-out width, sized to the headroom. */
  Coding: 'coding',
  /** A review or a reply on its thread: its reviewer wave from the free units, or the reserved slot. */
  Review: 'review',
  /** Every other model-consuming lane (scout, setup, watch, a model call): one stream. */
  Single: 'single',
} as const;
export type KeyAdmissionLane = (typeof KeyAdmissionLane)[keyof typeof KeyAdmissionLane];

/**
 * Every origin's lane, total so a new origin is a type error here rather than an
 * unsized lane. `Coding` is exactly `ORIGIN_CREDENTIAL_NEEDS_CODING_RUNNER` — the
 * harness lanes that fan editors out — and the partition test pins that.
 */
export const KEY_ADMISSION_LANE: Readonly<Record<TaskOrigin, KeyAdmissionLane>> = {
  [TaskOrigin.Conversation]: KeyAdmissionLane.Coding,
  [TaskOrigin.GhIssue]: KeyAdmissionLane.Coding,
  [TaskOrigin.Sentry]: KeyAdmissionLane.Coding,
  [TaskOrigin.Linear]: KeyAdmissionLane.Coding,
  [TaskOrigin.Manual]: KeyAdmissionLane.Coding,
  [TaskOrigin.PrReview]: KeyAdmissionLane.Review,
  [TaskOrigin.PrReviewReply]: KeyAdmissionLane.Review,
  [TaskOrigin.PrBabysit]: KeyAdmissionLane.Coding,
  [TaskOrigin.PrBabysitWatch]: KeyAdmissionLane.Single,
  [TaskOrigin.Scout]: KeyAdmissionLane.Single,
  [TaskOrigin.Setup]: KeyAdmissionLane.Single,
  [TaskOrigin.Devops]: KeyAdmissionLane.Coding,
  /** One model call offered to a machine (#6759): one stream. */
  [TaskOrigin.TriageJudge]: KeyAdmissionLane.Single,
  [TaskOrigin.KbFold]: KeyAdmissionLane.Single,
  [TaskOrigin.PlanAuthor]: KeyAdmissionLane.Single,
};

/**
 * FINISH BEFORE STARTING (owner, 2026-09-24: "many PRs still open on tesote.ai by
 * developerz.ai" — a 12 h-old PR with 14 unresolved review threads and two with red CI,
 * each needing a `pr_babysit` run on the one z.ai sub). When a key's slots are scarce,
 * the work that lands an OPEN pull request goes first, and a new coding task never
 * takes the last slot while such work waits. Lower rank = admitted first:
 *
 *   0 `FinishPr`   — `pr_babysit` (fix CI, answer review threads) and its CI watch, on
 *                    fleet PRs that are already open. Oldest first is the claim's own
 *                    `created_at` order within the rank.
 *   1 `Review`     — a review or a reply on its thread: the step that lets a PR merge.
 *   2 `ResumeWip`  — a coding task resuming saved work (a retry, or a park that is
 *                    resuming its saved commit): paid-for progress, closest to a PR.
 *   3 `NewCoding`  — a coding task starting from nothing.
 *   4 `FollowUp`   — scout, setup: onboarding and planning follow-ups.
 *
 * Enforced as a RESERVATION, not a queue: each waiting task of a better rank on the
 * account holds back one unit of every bounded key IT COULD RUN ON
 * ({@link reservedForHigherRanks}), so a task is admitted on a key only if a unit is left
 * after them. Which keys a waiting task could run on follows from its LANE, and the
 * caller resolves it (`waitingLaneKeyIds`, `@developerz/agent`): a lane bound to its
 * Agents-page selection waits on the selected key alone, in every hold state, and holds
 * nothing back on any other key; an unbound lane's parked task waits on the keys its
 * park named (`tasks.hold_key_ids`). On an unbounded key nothing is held back — there is
 * room for all of them.
 *
 * The ranks compare WITHIN the claim's task class, which leads them (`classRankSql`,
 * `@developerz/db`; #5992 "Interactive work still leads"): a waiting `coding` task never
 * holds a unit back from an `interactive` one, which the claim takes first (#6709). The
 * reverse still holds: a waiting `pr_review` (interactive, `Review`) reserves against coding
 * work and sessions by rank, per the tiers above and #5956.
 */
export const AdmissionRank = {
  FinishPr: 0,
  Review: 1,
  ResumeWip: 2,
  NewCoding: 3,
  FollowUp: 4,
} as const;
export type AdmissionRank = (typeof AdmissionRank)[keyof typeof AdmissionRank];

/**
 * Every origin's rank; a coding-lane origin (`'coding'`) splits on whether it resumes
 * saved work and whether the onboarding pass filed it. Exported because the area
 * gate's fairness arm renders the same order in SQL (`admissionRankSql`,
 * `@developerz/db`) — one table, two readers.
 */
export const ADMISSION_RANK_BY_ORIGIN: Readonly<Record<TaskOrigin, AdmissionRank | 'coding'>> = {
  [TaskOrigin.PrBabysit]: AdmissionRank.FinishPr,
  [TaskOrigin.PrBabysitWatch]: AdmissionRank.FinishPr,
  [TaskOrigin.PrReview]: AdmissionRank.Review,
  [TaskOrigin.PrReviewReply]: AdmissionRank.Review,
  [TaskOrigin.Conversation]: 'coding',
  [TaskOrigin.GhIssue]: 'coding',
  [TaskOrigin.Sentry]: 'coding',
  [TaskOrigin.Linear]: 'coding',
  [TaskOrigin.Manual]: 'coding',
  [TaskOrigin.Devops]: 'coding',
  [TaskOrigin.Scout]: AdmissionRank.FollowUp,
  [TaskOrigin.Setup]: AdmissionRank.FollowUp,
  /**
   * The offer inherits its CALLER's place in line (#6759). The triage batch is admitted
   * above `FinishPr` in-cluster (`packages/queue/src/session-admission.ts`), so its
   * machine offer takes the best rank this table has; the KB fold is gardener upkeep.
   */
  [TaskOrigin.TriageJudge]: AdmissionRank.FinishPr,
  [TaskOrigin.KbFold]: AdmissionRank.FollowUp,
  /** The Planner's plan call takes the scout's place in line: it continues the scout's ask (#7569). */
  [TaskOrigin.PlanAuthor]: AdmissionRank.FollowUp,
};

/**
 * `dispatched_by.source` on the tasks the onboarding/setup pass files as follow-ups
 * ("verify the dev scripts run clean", "align bin/check with CI", …). They are
 * `origin: manual` — plain coding work a worker runs — so the ORIGIN cannot say what
 * they are; this stamp is how {@link admissionRankOf} ranks them
 * {@link AdmissionRank.FollowUp} rather than as a user's new coding ask.
 */
export const SETUP_DISPATCH_SOURCE = 'setup';

/** Is this a coding-lane task the setup pass filed as a follow-up? */
export function isSetupFollowUp(
  origin: TaskOrigin,
  dispatchedBy: { readonly kind: string; readonly source?: string } | undefined,
): boolean {
  return (
    ADMISSION_RANK_BY_ORIGIN[origin] === 'coding' &&
    dispatchedBy?.kind === 'system' &&
    dispatchedBy.source === SETUP_DISPATCH_SOURCE
  );
}

/**
 * What ranks a task: its origin, whether it resumes saved work, whether setup filed it,
 * and whether it was minted more than {@link FOLLOW_UP_AGING_MS} ago. Every field is required, so
 * a reader that forgets one fails typecheck instead of ranking differently from the claim.
 */
export type AdmissionSubject = {
  readonly origin: TaskOrigin;
  readonly resumed: boolean;
  readonly setupFollowUp: boolean;
  /** Minted at least {@link FOLLOW_UP_AGING_MS} ago; a retry keeps its mint time (`agedSql`, `@developerz/db`). */
  readonly aged: boolean;
};

/**
 * How long after it was MINTED a {@link AdmissionRank.FollowUp} task ranks
 * {@link AdmissionRank.NewCoding} (#5715).
 *
 * THE FAILURE IT PREVENTS: a follow-up sorts behind every `NewCoding` task, so on a
 * saturated key, with same-account coding work arriving faster than slots free, it never
 * gets one. Prod, developerz-ai: the 2026-09-27..29 repo surveys first ran 171-239 h after
 * they were minted, overtaken by 20 newer priority-100 `manual` tasks, and a live survey
 * blocks the repo's next one, so each lost day cost a survey.
 *
 * WHY 6 H, from the minted-to-first-run wait. Measured on prod 2026-10-13, tasks minted
 * 2026-09-13 .. 2026-10-12 — re-run it rather than trust it:
 *
 *     SELECT t.envelope->>'origin' AS origin,
 *            coalesce(t.envelope->'dispatched_by'->>'source', '') AS source,
 *            count(*) AS n, count(r.started) AS ran,
 *            percentile_cont(0.9) WITHIN GROUP
 *              (ORDER BY extract(epoch FROM r.started - t.created_at) / 3600) AS p90_h,
 *            max(extract(epoch FROM r.started - t.created_at) / 3600) AS max_h
 *       FROM tasks t
 *       LEFT JOIN LATERAL (SELECT min(e.ts) AS started FROM task_run_events e
 *                           WHERE e.task_id = t.id) r ON true
 *      WHERE t.created_at >= '2026-09-13' AND t.created_at < '2026-10-13'
 *        AND (t.envelope->>'origin' IN ('scout', 'setup')
 *             OR t.envelope->'dispatched_by'->>'source' = 'setup')
 *      GROUP BY 1, 2 ORDER BY 1, 2;
 *
 * The follow-ups that DO get slots stay under it: scout-drain p90 0.2 h, max 0.3 (73 of
 * 73 ran); setup repo-onboarding p90 1.0 h, max 4.1 (94 of 98 ran). Repo surveys cross it:
 * p90 15.6 h, max 239 h (75 of 75), and so do scheduled scouts (p90 228.1 h, 6 of 6) and
 * some unsourced ones (p90 2.1 h, max 12.1, 18 of 18), which age the same way. 6 h leaves
 * 18 h before the next daily survey (`REPO_SURVEY_CRON`). A larger bound loses the next
 * day's survey first; a smaller one would bump healthy setup follow-ups.
 *
 * THE CLOCK IS `created_at`, AND RETRIES DO NOT RESET IT: a follow-up whose first run
 * took hours and failed re-queues already aged. Accepted, because the bound measures how
 * long the work has been owed, which a failed run does not shorten, and an aged retry
 * still gets only the one step below. A clock reset on re-queue would need a stored
 * column no other reader wants, and would let a run that keeps failing late re-enter at
 * `FollowUp` and starve again.
 *
 * ONE STEP, NEVER FURTHER: an aged follow-up ranks with new coding work, oldest first by
 * the claim's `created_at` order, and still yields to `FinishPr`, `Review` and
 * `ResumeWip`. Ranking it any higher would starve the PR lanes to feed surveys.
 */
export const FOLLOW_UP_AGING_MS = 6 * 3_600_000;

/**
 * The rank of a task. A coding origin splits three ways: a setup follow-up is a
 * {@link AdmissionRank.FollowUp} whether or not it resumes (it is onboarding work,
 * not a user's ask), otherwise resumed saved work outranks a fresh start. Then a
 * follow-up that is {@link AdmissionSubject.aged} moves up one rank to
 * {@link AdmissionRank.NewCoding} ({@link FOLLOW_UP_AGING_MS}).
 */
export function admissionRankOf(subject: AdmissionSubject): AdmissionRank {
  const rank = baseAdmissionRank(subject);
  return rank === AdmissionRank.FollowUp && subject.aged ? AdmissionRank.NewCoding : rank;
}

function baseAdmissionRank(subject: AdmissionSubject): AdmissionRank {
  const rank = ADMISSION_RANK_BY_ORIGIN[subject.origin];
  if (rank !== 'coding') return rank;
  if (subject.setupFollowUp) return AdmissionRank.FollowUp;
  return subject.resumed ? AdmissionRank.ResumeWip : AdmissionRank.NewCoding;
}

/**
 * A group of waiting tasks as the reservation reads it: who they are, their task class, how
 * many, and the keys they could take a slot on. `keyIds` null or absent = any key (an unbound lane's
 * unheld row, or a park that recorded none) — the conservative reading, which reserves
 * everywhere; an EMPTY list = none right now (a selection whose key is cooling), which
 * reserves nowhere.
 */
export type WaitingAdmissionWork = AdmissionSubject & {
  /**
   * The group's scheduling class, as the claim order reads it (`classRankSql`,
   * `@developerz/db`): an envelope with no `class` is `coding`. Required, so a reader that
   * forgets it fails typecheck instead of reserving across classes (#6709).
   */
  readonly class: TaskClass;
  readonly count: number;
  readonly keyIds?: readonly string[] | null | undefined;
  /**
   * The group's tasks share the claimant's priority and sort before it in the claim order
   * below priority: declaredness, the repo term (#5645), then age (`listWaitingWork`,
   * `@developerz/db`). Absent = false: a reader with no claimant (a
   * preview) has nothing to be older than.
   */
  readonly olderSamePriority?: boolean | undefined;
};

/**
 * Units `keyId` holds back for waiting tasks that outrank `rank`: one per task that could
 * take a slot ON THAT KEY. Per key, because a waiting task bound to other keys can never
 * use this one — counting it here parked coding work on idle subs behind reviews waiting
 * for their selected key.
 *
 * OUTRANK includes an OLDER PEER: a task of the same rank and priority that sorts first
 * (declaredness, repo term, then age; plan 101 R1, oldest-first within a priority). A parked row is invisible to the claim
 * until its window lapses, so without this a newer peer took every freed unit and the
 * parked one re-parked on a full key: prod 2026-10-01, 57 developerz-ai tasks held
 * `key_saturated` up to 148 h while 80 newer ones ran. The newer peer now parks instead,
 * and the claim order (rank, priority, declaredness, repo term, age) hands the unit to the
 * older one.
 *
 * CLASS SCOPE (#6709): `candidateClass` is the candidate's task class. A `coding` group
 * holds nothing back from an `interactive` candidate, because the claim sorts by class
 * before rank and takes the interactive task first; without this, waiting babysits parked
 * an interactive setup `key_reserved` for 90 min (prod 2026-10-23, tsk_90ceae4d) and took
 * the units it was refused. Every other pair compares by rank as above: an interactive
 * waiter still reserves against coding work (the reverse is not built; it needs its own
 * ruling).
 */
export function reservedForHigherRanks(
  rank: AdmissionRank,
  waiting: readonly WaitingAdmissionWork[],
  keyId: string,
  candidateClass: TaskClass,
): number {
  return waiting.reduce(
    (n, w) =>
      !claimTakesCandidateFirst(w.class, candidateClass) &&
      outranks(admissionRankOf(w), rank, w.olderSamePriority === true) &&
      (w.keyIds == null || w.keyIds.includes(keyId))
        ? n + w.count
        : n,
    0,
  );
}

/** A `coding` waiter sorts behind an `interactive` candidate in the claim, whatever its rank. */
function claimTakesCandidateFirst(waiting: TaskClass, candidate: TaskClass): boolean {
  return waiting === TaskClass.Coding && candidate === TaskClass.Interactive;
}

function outranks(waiting: AdmissionRank, self: AdmissionRank, olderPeer: boolean): boolean {
  return waiting < self || (waiting === self && olderPeer);
}

/**
 * Is any coding-lane task (any rank) waiting on the account beside this one? A waiting
 * coding task wants a width of its own, so the one being claimed takes its fair share.
 * Reviews and single-stream lanes take one unit each and never ask for a share; the ones
 * that outrank this task are already held back by {@link reservedForHigherRanks}.
 */
export function waitingCodingWork(
  waiting: readonly (Pick<AdmissionSubject, 'origin'> & { readonly count: number })[],
): boolean {
  return waiting.some(
    (w) => w.count > 0 && KEY_ADMISSION_LANE[w.origin] === KeyAdmissionLane.Coding,
  );
}
